// Screen: 01-entry-invitation
// Route: /invite/:token
// Opened via Telegram deep link. Shows subscription conditions before Accept / Decline.

import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Page } from '@/shared/ui/Page';
import { PageHeader } from '@/shared/ui/PageHeader';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { SecondaryButton } from '@/shared/ui/SecondaryButton';
import { InvitationCard } from '@/entities/subscription/InvitationCard';
import { subscriptionApi } from '@/entities/subscription/api';
import { useT } from '@/shared/lib/i18n';

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function Skeleton() {
  return (
    <div className="flex flex-col gap-3 animate-pulse">
      <div className="h-[200px] rounded-[24px] bg-white/40" />
      <div className="h-[180px] rounded-[20px] bg-white/30" />
      <div className="h-[52px] rounded-[16px] bg-white/30" />
      <div className="h-[52px] rounded-[16px] bg-white/20" />
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function InvitationPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const t = useT();

  const { data: invite, isLoading, isError } = useQuery({
    queryKey: ['invite', token],
    queryFn: () => subscriptionApi.getInviteByCode(token!),
    enabled: !!token,
    retry: false,
  });

  const acceptMutation = useMutation({
    mutationFn: () => subscriptionApi.acceptInvite(token!),
    onSuccess: (result) => {
      navigate(`/subscriptions/${result.subscription_id}`, { replace: true });
    },
  });

  const declineMutation = useMutation({
    mutationFn: () => subscriptionApi.declineInvite(token!),
    onSuccess: () => {
      navigate('/', { replace: true });
    },
  });

  return (
    <Page withDock={false}>
      <PageHeader title={<span className="font-bold text-[19px]">{t('invitation.title')}</span>} />

      <div className="px-[18px] pb-8 flex flex-col gap-4 flex-1">
        {isLoading && <Skeleton />}

        {isError && (
          <div className="flex flex-col items-center gap-4 py-16 text-center">
            <span className="text-[48px]">🔗</span>
            <div>
              <p className="text-[16px] font-semibold text-text-primary">
                {t('invitation.invalidTitle')}
              </p>
              <p className="text-[13px] text-text-secondary mt-1">
                {t('invitation.invalidText')}
              </p>
            </div>
            <button
              className="mt-2 text-[14px] text-brand-green font-semibold"
              onClick={() => navigate('/', { replace: true })}
            >
              {t('invitation.home')}
            </button>
          </div>
        )}

        {invite && (
          <>
            <InvitationCard invite={invite} />

            <div className="flex-1" />

            <div className="flex flex-col gap-3 pt-2">
              {acceptMutation.isError && (
                <p className="text-center text-[13px] text-status-debt">
                  {t('invitation.acceptError')}
                </p>
              )}

              <PrimaryButton
                onClick={() => acceptMutation.mutate()}
                loading={acceptMutation.isPending}
                disabled={declineMutation.isPending}
              >
                {t('invitation.accept')}
              </PrimaryButton>

              <SecondaryButton
                onClick={() => declineMutation.mutate()}
                loading={declineMutation.isPending}
                disabled={acceptMutation.isPending}
              >
                {t('invitation.decline')}
              </SecondaryButton>
            </div>
          </>
        )}
      </div>
    </Page>
  );
}
